import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toonAantal } from '@boodschappenlijst/shared';
import { ProductForm } from '../../components/ProductForm';
import { ApiError } from '../../lib/apiClient';
import { useToast } from '../../lib/useToast';
import { vastApi } from './vast.api';

/**
 * De vaste producten: wat er elke keer weer op de lijst hoort.
 *
 * Aanvinken en in één handeling doorzetten. Producten die al openstaan slaat de
 * server over — dat wordt hier gemeld, want stilzwijgend overslaan laat de
 * gebruiker denken dat de knop niet werkte.
 */
export function VasteProducten({ onGewijzigd }: { onGewijzigd: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [gekozen, setGekozen] = useState<Set<string>>(new Set());
  const [toevoegen, setToevoegen] = useState(false);

  const producten = useQuery({ queryKey: ['vaste-producten'], queryFn: vastApi.list });

  const ververs = () => void qc.invalidateQueries({ queryKey: ['vaste-producten'] });
  const meldFout = (err: unknown) =>
    toast(err instanceof ApiError ? err.message : 'Er ging iets mis', true);

  const maak = useMutation({
    mutationFn: vastApi.create,
    onSuccess: () => {
      ververs();
      setToevoegen(false);
      toast('Toegevoegd aan de vaste producten');
    },
    onError: meldFout,
  });

  const verwijder = useMutation({
    mutationFn: vastApi.remove,
    onSuccess: ververs,
    onError: meldFout,
  });

  const opLijst = useMutation({
    mutationFn: vastApi.opLijst,
    onSuccess: ({ toegevoegd, overgeslagen }) => {
      onGewijzigd();
      setGekozen(new Set());
      if (!toegevoegd.length && overgeslagen.length) {
        toast(`Stond al op de lijst: ${overgeslagen.join(', ')}`);
      } else if (overgeslagen.length) {
        toast(`${toegevoegd.length} toegevoegd, al op de lijst: ${overgeslagen.join(', ')}`);
      } else {
        toast(`${toegevoegd.length} toegevoegd aan de lijst`);
      }
    },
    onError: meldFout,
  });

  const wissel = (id: string) =>
    setGekozen((oud) => {
      const nieuw = new Set(oud);
      if (nieuw.has(id)) nieuw.delete(id);
      else nieuw.add(id);
      return nieuw;
    });

  const lijst = producten.data?.producten ?? [];
  const allesGekozen = lijst.length > 0 && gekozen.size === lijst.length;

  return (
    <section>
      <h2 className="vs-section-title">Vaste producten</h2>

      {producten.isPending ? (
        <p className="vs-empty">Laden…</p>
      ) : producten.isError ? (
        <p className="vs-empty">De vaste producten konden niet worden geladen.</p>
      ) : (
        <div className="vs-card">
          {lijst.length === 0 ? (
            <p className="vs-empty vs-empty--inline">
              Nog geen vaste producten. Zet hier wat er elke keer weer op de lijst moet.
            </p>
          ) : (
            <>
              <div className="vs-vast__acties">
                <button
                  className="vs-filter"
                  onClick={() =>
                    setGekozen(allesGekozen ? new Set() : new Set(lijst.map((p) => p.id)))
                  }
                >
                  {allesGekozen ? 'Niets selecteren' : 'Alles selecteren'}
                </button>
                <button
                  className="vs-btn vs-btn--sm"
                  disabled={gekozen.size === 0 || opLijst.isPending}
                  onClick={() => opLijst.mutate([...gekozen])}
                >
                  {opLijst.isPending
                    ? 'Bezig…'
                    : `Op de lijst${gekozen.size ? ` (${gekozen.size})` : ''}`}
                </button>
              </div>

              <ul className="vs-list">
                {lijst.map((p) => (
                  <li key={p.id} className="vs-row">
                    <input
                      className="vs-row__check"
                      type="checkbox"
                      checked={gekozen.has(p.id)}
                      onChange={() => wissel(p.id)}
                      aria-label={`${p.naam} selecteren`}
                    />
                    <div className="vs-row__body">
                      <div className="vs-row__naam">{p.naam}</div>
                    </div>
                    <span className="vs-row__aantal">{toonAantal(p.aantal, p.eenheid)}</span>
                    <button
                      className="vs-icon-btn"
                      onClick={() => verwijder.mutate(p.id)}
                      aria-label={`${p.naam} uit de vaste producten halen`}
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}

          {toevoegen ? (
            <div className="vs-vast__formulier">
              <ProductForm
                idPrefix="vast"
                knopLabel="Vastzetten"
                bezig={maak.isPending}
                onSubmit={async (waarden) => {
                  await maak.mutateAsync(waarden);
                  return true;
                }}
              />
              <button className="vs-linkknop" onClick={() => setToevoegen(false)}>
                Annuleren
              </button>
            </div>
          ) : (
            <button className="vs-linkknop" onClick={() => setToevoegen(true)}>
              + Vast product toevoegen
            </button>
          )}
        </div>
      )}
    </section>
  );
}
